import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
    createAssessmentTemplate,
    deleteAssessmentTemplate,
    fetchAssessmentTemplatesList,
    type AssessmentTemplatesListQueryParams,
} from '@/api/assessment-templates.api'
import type { CreateAssessmentTemplateBodySchema } from '@/generated/api-client'
import { useToast } from '@/app/context/toast-provider'
import { assessmentTemplatesQueryKeys } from '@/constants/cache-keys.contant'
import { invalidateOperation } from '@/lib/api/query-invalidation'

export const useAssessmentTemplatesListQuery = (params: AssessmentTemplatesListQueryParams, options?: { enabled?: boolean }) => {
    return useQuery({
        queryKey: assessmentTemplatesQueryKeys.list(params),
        queryFn: () => fetchAssessmentTemplatesList(params),
        enabled: options?.enabled !== false && Boolean(params.companySlug),
        staleTime: 0,
        placeholderData: keepPreviousData,
    })
}

export const useCreateAssessmentTemplateMutation = () => {
    const queryClient = useQueryClient()
    const { showSuccessToast, showErrorToast } = useToast()

    return useMutation({
        mutationFn: (body: CreateAssessmentTemplateBodySchema) => createAssessmentTemplate(body),
        onSuccess: ({ message }) => {
            showSuccessToast(message ?? 'Assessment template created successfully')
            void queryClient.invalidateQueries({ queryKey: assessmentTemplatesQueryKeys.all })
            void invalidateOperation(queryClient, 'getAssessmentTemplates')
        },
        onError: (error: Error) => {
            showErrorToast(error.message || 'Failed to create assessment template')
        },
    })
}

export const useDeleteAssessmentTemplateMutation = () => {
    const queryClient = useQueryClient()
    const { showSuccessToast, showErrorToast } = useToast()

    return useMutation({
        mutationFn: ({ id, companySlug }: { id: number | string; companySlug: string }) => deleteAssessmentTemplate(id, { companySlug }),
        onSuccess: ({ message }, variables) => {
            showSuccessToast(message ?? 'Assessment template deleted successfully')
            void queryClient.invalidateQueries({ queryKey: assessmentTemplatesQueryKeys.all })
            void invalidateOperation(queryClient, 'getAssessmentTemplates')
            void queryClient.removeQueries({
                queryKey: assessmentTemplatesQueryKeys.detail(variables.id, variables.companySlug),
            })
        },
        onError: (error: Error) => {
            showErrorToast(error.message || 'Failed to delete assessment template')
        },
    })
}
